import SEO from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';

const LegalPage = ({ titleEn, titleAr, lastUpdatedEn, lastUpdatedAr, seo, children }) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const title = isAr ? titleAr : titleEn;
  const lastUpdated = isAr ? lastUpdatedAr : lastUpdatedEn;
  return (
    <>
      {seo && (
        <SEO
          titleEn={seo.titleEn}
          titleAr={seo.titleAr}
          descEn={seo.descEn}
          descAr={seo.descAr}
          path={seo.path}
        />
      )}
      <div className="pt-16">
        <section className="py-20 lg:py-24 bg-background">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-foreground mb-2">{title}</h1>
              <p className="text-sm text-muted-foreground">{lastUpdated}</p>
            </div>
            <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">
              {children}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-base font-semibold text-foreground mt-8 mb-3">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const TermsPage = () => (
  <LegalPage
    titleEn="Terms of Service"
    titleAr="شروط الخدمة"
    lastUpdatedEn="Last updated: January 1, 2025"
    lastUpdatedAr="آخر تحديث: ١ يناير ٢٠٢٥"
    seo={{
      titleEn: 'Terms of Service | Quidory',
      titleAr: 'شروط الخدمة | كويدوري',
      descEn: "Read Quidory's terms of service. Understand your rights and responsibilities when using our AI intelligence and publishing platform.",
      descAr: 'اقرأ شروط الخدمة لكويدوري. تعرف على حقوقك والتزاماتك عند استخدام منصة الذكاء الاصطناعي والنشر الخاصة بنا.',
      path: '/terms',
    }}
  >
    <Section title="1. Acceptance of Terms">
      <p>By accessing or using Quidory's platform, website, or services ("Services"), provided by <strong className="text-foreground">Al Yakhour LLC</strong> (registered in Wyoming, USA, operating under the brand name "Quidory"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Services.</p>
      <p>These Terms apply to all users, visitors, and others who access or use the Services.</p>
    </Section>

    <Section title="2. Description of Services">
      <p>Quidory provides an AI-powered multilingual intelligence and publishing platform that includes:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li>QuidPulse: AI-curated multilingual crypto intelligence feed</li>
        <li>Quidanalyzer: AI-powered conversational analysis assistant</li>
        <li>Post Publisher: Automated multilingual content publishing system</li>
        <li>Video Publisher: AI-powered multilingual video production and distribution</li>
      </ul>
    </Section>

    <Section title="3. Account Registration">
      <p>To access certain features, you must register for an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.</p>
      <p>You are responsible for safeguarding the password that you use to access the Services and for any activities or actions under your account. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.</p>
    </Section>

    <Section title="4. Subscription and Payments">
      <p>Quidory offers both free and paid subscription plans. By subscribing to a paid plan, you agree to pay the applicable fees. Fees are charged in advance on a monthly or annual basis and are non-refundable except as required by law.</p>
      <p>We reserve the right to modify our pricing at any time. We will provide at least 30 days' notice of any price changes.</p>
    </Section>

    <Section title="5. User Obligations">
      <p>You agree not to use the Services to:</p>
      <ul className="list-disc ps-5 space-y-1.5 mt-2">
        <li>Violate any applicable laws or regulations</li>
        <li>Infringe on intellectual property rights of others</li>
        <li>Transmit spam, unsolicited communications, or malware</li>
        <li>Engage in market manipulation or financial fraud</li>
        <li>Impersonate any person or entity</li>
        <li>Collect or harvest user information without consent</li>
        <li>Interfere with or disrupt the platform's infrastructure</li>
      </ul>
    </Section>

    <Section title="6. Content Ownership">
      <p><strong>Your Content:</strong> You retain all ownership rights to the content you create using Quidory. By using our Services, you grant Quidory a limited license to process and display your content solely as necessary to provide the Services.</p>
      <p><strong>Quidory's Content:</strong> The platform, its design, algorithms, and Quidory-generated intelligence content are owned by Quidory and protected by intellectual property laws.</p>
    </Section>

    <Section title="7. AI-Generated Content Disclaimer">
      <p>Quidory uses artificial intelligence to generate intelligence reports, analysis, and content. This content is provided for informational purposes only and does not constitute financial advice. Always consult a qualified financial advisor before making investment decisions.</p>
    </Section>

    <Section title="8. Limitation of Liability">
      <p>To the maximum extent permitted by law, Quidory shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business opportunities, arising out of or in connection with these Terms or your use of the Services.</p>
    </Section>

    <Section title="9. Termination">
      <p>We may terminate or suspend your account at any time, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination, your right to use the Services will immediately cease.</p>
    </Section>

    <Section title="10. Changes to Terms">
      <p>We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page. Your continued use of the Services after any changes constitutes your acceptance of the new Terms.</p>
    </Section>

    <Section title="11. Contact">
      <p>If you have any questions about these Terms, please contact us at <strong>support@quidory.com</strong>.</p>
      <p>
        <strong className="text-foreground">Legal Entity:</strong> Al Yakhour LLC<br />
        <strong className="text-foreground">Registered Office:</strong> Wyoming, USA<br />
        <strong className="text-foreground">Website:</strong> https://quidory.com
      </p>
    </Section>
  </LegalPage>
);

export default TermsPage;
